import { z } from 'zod';
import {
  openAiFileDetailSchema,
  storedProjectFilePipelineAiStepSchema,
} from './ai/project-file-pipeline-ai.model';
import { OPEN_AI_REASONING_EFFORTS } from './ai/open-ai-reasoning.model';

/** Token usage reported by the provider for one call. */
export const projectFileAiCallUsageSchema = z.object({
  inputTokens: z.number(),
  /** Part of {@link inputTokens} billed at the cached rate. */
  cachedInputTokens: z.number(),
  /** Part of {@link inputTokens} written to the prompt cache (1.25× on GPT-5.6+). */
  cacheWriteTokens: z.number(),
  outputTokens: z.number(),
  /** Part of {@link outputTokens} spent on reasoning. */
  reasoningTokens: z.number(),
});
export type ProjectFileAiCallUsage = z.infer<
  typeof projectFileAiCallUsageSchema
>;

const projectFileAiCallBaseSchema = z.object({
  step: storedProjectFilePipelineAiStepSchema,
  /** Null for batch-level calls (joined blueprint, consolidation). */
  fileId: z.string().nullable(),
  /** Segment index for extraction, building group for joined; null otherwise. */
  subject: z.string().nullable(),
  model: z.string(),
  reasoningEffort: z.enum(OPEN_AI_REASONING_EFFORTS).nullable(),
  /** Null when the call attached no file. */
  fileDetail: openAiFileDetailSchema.nullable(),
  startedAt: z.string(),
  durationMs: z.number(),
});

/** One model call made by the project-file pipeline, appended to `project_file_batches.ai_calls`. */
export const projectFileAiCallSchema = z.discriminatedUnion('outcome', [
  projectFileAiCallBaseSchema.extend({
    outcome: z.literal('ok'),
    /** Null when the provider omitted usage. */
    usage: projectFileAiCallUsageSchema.nullable(),
  }),
  projectFileAiCallBaseSchema.extend({
    outcome: z.literal('failed'),
    error: z.string(),
  }),
]);
export type ProjectFileAiCall = z.infer<typeof projectFileAiCallSchema>;
